import type {
	RecoveryFlow,
	UpdateRecoveryFlowWithCodeMethod,
} from "@ory/kratos-client";
import type { AxiosError } from "axios";
import type React from "react";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import {
	ContentContainer,
	ContentContainerWrapper,
} from "~/components/ContentContainer";
import { Flow, handleFlowError } from "~/components/KratosFlow";
import { DefaultLayout } from "~/components/Layout";
import { Loading } from "~/components/Loading";
import { useModal } from "~/components/Modal";
import { useSnackBar } from "~/components/SnackBar";
import { kratos } from "~/ory";
import { StepBar } from "~/pages/Activate/StepBar";
import { createFlowErrorHandler } from "~/utils/errorHandler";

export const ActivateSentEmail: React.FC = () => {
	const [flow, setFlow] = useState<RecoveryFlow | undefined>();

	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const [openModal, closeModal] = useModal();
	const [openSnackBar] = useSnackBar();
	// Get ?flow=... from the URL
	const flowId = searchParams.get("flow");

	// In this effect we either initiate a new registration flow, or we fetch an existing registration flow.
	// biome-ignore lint/correctness/useExhaustiveDependencies: openSnackBar/openModal/closeModal are intentionally excluded. They are used only for fire-and-forget error display, and this effect should re-fetch on session/flow/URL changes only. They are stable in production (SnackBar/Modal providers memoize open/close via useCallback), so excluding them changes nothing at runtime; biome just cannot verify that stability across the context boundary.
	useEffect(() => {
		// If the router is not ready yet, or we already have a flow, do nothing.
		if (flow) {
			return;
		}

		// Fetching/initializing a flow can fail at the network level (request aborted,
		// offline) with no HTTP response. handleFlowError can't classify those and re-rejects,
		// so we add a terminal handler to avoid an unhandled promise rejection (mirrors submit()).
		const onFlowError = createFlowErrorHandler(
			openSnackBar,
			openModal,
			closeModal,
		);

		// If ?flow=.. was in the URL, we fetch it
		if (flowId) {
			kratos
				.getRecoveryFlow({ id: String(flowId) })
				.then(({ data }) => {
					// We received the flow - let's use its data and render the form!
					setFlow(data);
				})
				.catch(handleFlowError(navigate, "recovery", setFlow))
				.catch(onFlowError);
			return;
		}

		// Otherwise we initialize it
		kratos
			.createBrowserRecoveryFlow()
			.then(({ data }) => {
				setFlow(data);
			})
			.catch(handleFlowError(navigate, "recovery", setFlow))
			.catch(onFlowError);
	}, [flowId, flow, navigate]);

	if (!flow) return <Loading />;

	const onSubmit = (
		values: UpdateRecoveryFlowWithCodeMethod & { method: "code" },
	) => {
		setSearchParams({ flow: String(flow?.id) });
		return kratos
			.updateRecoveryFlow({
				flow: String(flow?.id),
				updateRecoveryFlowBody: values,
			})
			.then(({ data }) => {
				// Form submission was successful, show the message to the user!
				setFlow(data);
			})
			.catch(handleFlowError(navigate, "recovery", setFlow))
			.catch((err: AxiosError<RecoveryFlow>) => {
				// If the previous handler did not catch the error it's most likely a form validation error
				if (err.response?.status === 400) {
					// Yup, it is!
					setFlow(err.response?.data);
					return;
				}

				createFlowErrorHandler(openSnackBar, openModal, closeModal)(err);
			});
	};

	return (
		<>
			<title>Activate Account | TIER IV Account</title>
			<DefaultLayout>
				<ContentContainerWrapper>
					<ContentContainer>
						<h1 className="mt-10 text-center font-normal">ACTIVATE ACCOUNT</h1>
						<div className="text-center mt-6">
							<StepBar step={2} />
						</div>
						<Flow
							onSubmit={onSubmit}
							flow={flow}
							context={{
								withIcon: true,
							}}
							setLabelTexts={[
								{
									query: { name: "method", type: "submit", value: "code" },
									value: "Submit",
								},
							]}
							setLabelTypes={[
								{
									query: { name: "email", type: "submit" },
									value: "success",
								},
							]}
							nodeDisablers={[
								{
									target: { name: "method" },
									keys: ["code"],
									allowEmpty: false,
									allowNotChanged: true,
								},
							]}
						/>
					</ContentContainer>
				</ContentContainerWrapper>
			</DefaultLayout>
		</>
	);
};
